import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Product, CreateProductData } from '../types';
import { getCurrentCurrency } from '../config';
import ImageUpload from './ImageUpload';

interface ProductFormProps {
  product?: Product | null;
  onSave: (data: CreateProductData) => void;
  onCancel: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState<CreateProductData>({
    name: '',
    price: 0,
    image_url: '',
    description: '',
    category: '',
    stock_quantity: 0,
  });

  const [errors, setErrors] = useState<Partial<CreateProductData>>({});

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        price: product.price,
        image_url: product.image_url,
        description: product.description,
        category: product.category,
        stock_quantity: product.stock_quantity,
      });
    }
  }, [product]);

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateProductData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }

    if (formData.price <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    if (formData.stock_quantity < 0) {
      newErrors.stock_quantity = 'Stock quantity cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'price' || name === 'stock_quantity' ? parseFloat(value) || 0 : value,
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof CreateProductData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const categories = [
    'Vegetables',
    'Fruits',
    'Dairy & Honey',
    'Bakery',
    'Meat & Fish',
    'Grains & Legumes',
    'Herbs & Spices',
    'Other'
  ];

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="modal-close" onClick={onCancel}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="name">
              Product Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className={`form-input ${errors.name ? 'error' : ''}`}
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter product name"
            />
            {errors.name && <div className="error-text">{errors.name}</div>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="price">
                Price ({getCurrentCurrency().symbol}) *
              </label>
              <input
                type="number"
                id="price"
                name="price"
                className={`form-input ${errors.price ? 'error' : ''}`}
                value={formData.price}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              {errors.price && <div className="error-text">{errors.price}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="stock_quantity">
                Stock Quantity
              </label>
              <input
                type="number"
                id="stock_quantity"
                name="stock_quantity"
                className={`form-input ${errors.stock_quantity ? 'error' : ''}`}
                value={formData.stock_quantity}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
              {errors.stock_quantity && <div className="error-text">{errors.stock_quantity}</div>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="category">
              Category
            </label>
            <select
              id="category"
              name="category"
              className="form-input"
              value={formData.category}
              onChange={handleInputChange}
            >
              <option value="">Select a category</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Product Image
            </label>
            <ImageUpload
              onImageSelect={(imageUrl) => {
                setFormData(prev => ({ ...prev, image_url: imageUrl }));
              }}
              currentImageUrl={formData.image_url}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              className="form-input"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter product description"
              rows={3}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="submit" className="btn" style={{ flex: 1 }}>
              {product ? 'Update Product' : 'Add Product'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onCancel} style={{ flex: 1 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
